import { useState } from 'react';
import { cabinetApi, formatDate } from '../../api/cabinet';
import { errorMessage } from '../../api/client';
import {
  ABILITY_KIND_LABELS,
  FORM_FIELDS,
  FORM_FIELD_LABELS,
  type ModerationTarget,
} from '../../api/types';
import { t } from '../../i18n';
import { Action, Gap, Muted, Prose } from '../../shared/ui';

/**
 * Раскрывашка версии в истории.
 *
 * У имущества это «что было в этой версии» — единственный способ прочитать
 * отклонённую или снятую: в карточке висит принятая, и редактор засеивается
 * ею же.
 *
 * У АНКЕТЫ — «что изменилось». По истории игрок ищет не текст версии (он
 * длинный, тринадцать полей, и почти весь совпадает с соседними), а свою
 * правку: что он тогда поменял и что за это ответил модератор. Какие поля
 * тронуты, считает сервер и кладёт в `diffFieldCodes` — на клиенте тексты не
 * сравниваем, у сервера они приведены к одному виду.
 *
 * Читаем по требованию, а не вместе со списком: списки версий текста больше не
 * возят, в этом и был смысл лёгкой истории.
 */
export function VersionDetails({
  target,
  versionId,
}: {
  target: ModerationTarget;
  versionId: number;
}) {
  const [body, setBody] = useState<VersionBody | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = () => {
    if (body !== null) {
      setBody(null);
      return;
    }
    setLoading(true);
    setError(null);
    loadVersion(target, versionId)
      .then((loaded) => setBody(loaded))
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <Gap step="s2" />
      {/* Подчёркнутая подпись, а не кнопка в рамке: это раскрывашка внутри
          карточки, и рамка ставила её вровень с «править» и «в архив». Под
          кнопкой при этом настоящий button — вкуишный Link с onClick рисуется
          span'ом, и Tab его обходит. */}
      <Action tone="link" size="s" aria-expanded={body !== null} onClick={toggle}>
        {body !== null
          ? t('common.action.collapse')
          : target === 'form'
            ? t('property.version.changed')
            : t('property.version.open')}
      </Action>

      {loading ? (
        <>
          <Gap step="s2" />
          <Muted block>{t('property.version.loading')}</Muted>
        </>
      ) : null}
      {error !== null ? (
        <>
          <Gap step="s2" />
          <Muted block tone="negative">
            {error}
          </Muted>
        </>
      ) : null}

      {body !== null ? (
        <>
          <Gap step="s2" />
          {body.note === null ? null : (
            <>
              <Muted block>{body.note}</Muted>
              <Gap step="s1h" />
            </>
          )}
          {body.fields.length === 0 && body.note === null ? (
            <Muted>{t('property.version.empty')}</Muted>
          ) : (
            body.fields.map((field) => (
              <div key={field.label}>
                <Muted>{field.label}</Muted>
                <Prose>{field.value}</Prose>
                <Gap step="s1h" />
              </div>
            ))
          )}
        </>
      ) : null}
    </div>
  );
}

interface VersionField {
  label: string;
  value: string;
}

interface VersionBody {
  fields: VersionField[];
  /** Строка о самой версии: первая, или в ней ничего не поменялось. */
  note: string | null;
}

/** Пустые поля не показываем: читать в них нечего. */
function filled(pairs: Array<[string, string | null | undefined]>): VersionField[] {
  return pairs
    .filter((pair): pair is [string, string] => (pair[1] ?? '').trim() !== '')
    .map(([label, value]) => ({ label, value }));
}

async function loadVersion(target: ModerationTarget, versionId: number): Promise<VersionBody> {
  if (target === 'item') {
    const version = await cabinetApi.itemVersion(versionId);
    return {
      fields: filled([
        [t('common.field.title'), version.title],
        [t('common.field.description'), version.description],
      ]),
      note: null,
    };
  }

  if (target === 'pet') {
    const version = await cabinetApi.petVersion(versionId);
    return {
      fields: filled([
        [t('common.field.nickname'), version.name],
        [t('common.field.kind'), version.species],
        [t('common.field.description'), version.description],
      ]),
      note: null,
    };
  }

  if (target === 'ability') {
    const version = await cabinetApi.abilityVersion(versionId);
    return {
      fields: filled([
        [t('common.field.kind'), ABILITY_KIND_LABELS[version.kind]],
        [t('common.field.title'), version.title],
        [t('common.field.description'), version.description],
        [t('common.field.source'), version.sourceLink],
        [
          t('ability.field.expiresAt'),
          version.expiresAt === null ? null : formatDate(version.expiresAt),
        ],
      ]),
      note: null,
    };
  }

  const version = await cabinetApi.formVersion(versionId);
  const text = FORM_FIELDS.filter(
    (field) => field !== 'abilities' && field !== 'appearanceImageId',
  );

  // Базы нет — это первая версия, и «изменилось» в ней всё: показываем целиком.
  if (version.diffFieldCodes === null) {
    return {
      fields: filled(text.map((field) => [FORM_FIELD_LABELS[field], version[field]])),
      note: t('property.version.first'),
    };
  }

  // Очищенное поле — тоже правка, и молча пропустить его нельзя: иначе
  // «что изменилось» умолчит ровно о том, что человек стёр.
  const changed = new Set<string>(version.diffFieldCodes);
  const fields = text
    .filter((field) => changed.has(field))
    .map((field) => {
      const value = (version[field] ?? '').trim();
      return {
        label: FORM_FIELD_LABELS[field],
        value: value === '' ? t('property.version.cleared') : value,
      };
    });

  return { fields, note: fields.length === 0 ? t('property.version.same') : null };
}
